import React, { useContext, useRef, useEffect, useState } from 'react'
import { AppContext } from '../AppContext';
import { Audios, QuranAudios } from '../data/Audios';
import { FontAwesome } from '../data/FontAwesome';

export default function AudioPlayer() {

    const { locationSettings, dol, setIsAudioPlaying, t } = useContext(AppContext)
    const playerDiv = useRef(null)
    const playButtonDiv = useRef(null)
    const audioPlayer = useRef(null)
    const [isPaused, setIsPaused] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [currentTrack, setCurrentTrack] = useState({ type: t('media.azan'), title: '' })

    const markPendingAudioAsPlayed = () => {
        const pendingAudio = JSON.parse(localStorage.getItem("AAA"));
        if (!pendingAudio) return;
        pendingAudio.isPlayed = true;
        localStorage.setItem("AAA", JSON.stringify(pendingAudio));
    }

    useEffect(() => {

        if (!audioPlayer.current.paused) {
            dol('Already playing audio.')
            return;
        }

        let AU = JSON.parse(localStorage.getItem("AAA"));
        if (AU) {
            const requestedAt = Number(AU.requestedAt) || Date.now();
            const isStillRelevant = Date.now() - requestedAt <= 5 * 60 * 1000;
            if (!AU.isPlayed && isStillRelevant) {
                let audio = Audios.find(a => a.id === AU.id);
                if (!audio) {
                    localStorage.removeItem("AAA");
                    return;
                }
                audioPlayer.current.src = audio.source;
                setCurrentTrack({ type: audio.isAlarm ? t('media.reminder') : t('media.azan'), title: audio.name });

                let promise = audioPlayer.current.play();
                if (promise) {
                    promise.then(_ => {
                        markPendingAudioAsPlayed();
                        dol('Azan called: ' + locationSettings.address);
                        playerDiv.current.style.visibility = 'visible';
                        playButtonDiv.current.style.visibility = 'hidden';
                    }).catch(error => {
                        dol(error);
                        playerDiv.current.style.visibility = 'hidden';
                        playButtonDiv.current.style.visibility = 'visible';
                    });
                }
            } else if (!isStillRelevant) {
                localStorage.removeItem("AAA");
                playButtonDiv.current.style.visibility = 'hidden';
            }
        }


        let QA = JSON.parse(localStorage.getItem("QuranAudio"));
        if (QA) {
            localStorage.removeItem("QuranAudio");
            let audio = QuranAudios.find(a => a.id === QA.id);
            audioPlayer.current.src = audio.mp3;
            setCurrentTrack({ type: t('media.recitation'), title: `${audio.name} · ${audio.reciter}` });

            let promise = audioPlayer.current.play();
            if (promise) {
                promise.then(_ => {
                    // autoplay started!
                    playerDiv.current.style.visibility = 'visible';
                    playButtonDiv.current.style.visibility = 'hidden';
                }).catch(error => {
                    dol(error);
                    playerDiv.current.style.visibility = 'hidden';
                    playButtonDiv.current.style.visibility = 'visible';
                                    });
            }
        }

    }, [dol, t, locationSettings.address])

    const stopAudio = () => {
        setIsAudioPlaying(false);
        audioPlayer.current.pause();
        audioPlayer.current.currentTime = 0;
        playerDiv.current.style.visibility = 'hidden';
        playButtonDiv.current.style.visibility = 'hidden';
            }

    const togglePause = () => {
        if (audioPlayer.current.paused)
            playAudio();
        else
            audioPlayer.current.pause();
    }

    const audioPaused = () => {
        if (audioPlayer.current.ended) return;
        setIsAudioPlaying(false);
        setIsPaused(true);
    }

    const audioPlayed = () => {
        setIsAudioPlaying(true)
        setIsPaused(false);
    }

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    }

    const onTimeUpdate = () => {
        setCurrentTime(audioPlayer.current.currentTime);
    }

    const onLoadedMetadata = () => {
        setDuration(audioPlayer.current.duration);
    }

    const onSliderChange = (e) => {
        const time = parseFloat(e.target.value);
        audioPlayer.current.currentTime = time;
        setCurrentTime(time);
    }

    const rewind5 = () => {
        audioPlayer.current.currentTime = Math.max(0, audioPlayer.current.currentTime - 5);
    }

    const playAudio = () => {
        const promise = audioPlayer.current.play();
        if (promise) {
            promise.then(() => {
                markPendingAudioAsPlayed();
                playerDiv.current.style.visibility = 'visible';
                playButtonDiv.current.style.visibility = 'hidden';
            }).catch(dol);
        }
    }

    return (
        <>
            <audio id="audioPlayer" ref={audioPlayer} onPlay={audioPlayed} onPause={audioPaused} onEnded={stopAudio} onTimeUpdate={onTimeUpdate} onLoadedMetadata={onLoadedMetadata} style={{ display: 'none' }} />
            <div ref={playerDiv} className="audioPlayerFixed" role='region' aria-label={t('media.player')}>
                <div className='audio-player-card'>
                    <div className='audio-player-heading'>
                        <span className='audio-player-icon' aria-hidden='true'>{currentTrack.type === t('media.recitation') ? FontAwesome.Book : FontAwesome.Bell}</span>
                        <span className='audio-player-copy'>
                            <small>{currentTrack.type} · {isPaused ? t('media.paused') : t('media.nowPlaying')}</small>
                            <strong>{currentTrack.title || t('media.audio')}</strong>
                        </span>
                        <button className='audio-player-stop' title={t('media.stop')} aria-label={t('media.stop')} onClick={stopAudio}>{FontAwesome.Stop}</button>
                    </div>
                    <div className='audio-player-progress'>
                        <span>{formatTime(currentTime)}</span>
                        <input type='range' aria-label={t('media.progress')} min={0} max={duration || 0} step={0.1} value={currentTime} onChange={onSliderChange} />
                        <span>{formatTime(duration)}</span>
                    </div>
                    <div className='audio-player-controls'>
                        <button className='audio-player-control' title={t('media.rewind')} aria-label={t('media.rewind')} onClick={rewind5}>{FontAwesome.Backward}<small>5</small></button>
                        <button className='audio-player-control audio-player-control-main' title={isPaused ? t('media.play') : t('media.pause')} aria-label={isPaused ? t('media.play') : t('media.pause')} onClick={togglePause}>{isPaused ? FontAwesome.Play : FontAwesome.Pause}</button>
                    </div>
                </div>
            </div>
            <div ref={playButtonDiv} onClick={playAudio} className="audioButtonDiv">
                <div className='audio-play-request'>
                    <span>{FontAwesome.Play}</span>
                    <strong>{t('settings.tapToPlay')}</strong>
                    <small>{currentTrack.title}</small>
                </div>
            </div>
        </>
    )
}
